import React, { forwardRef, useImperativeHandle, useState, useRef, useEffect } from 'react';
import { useWindowDimensions, Platform, Pressable, Alert } from 'react-native';
import { MotiView } from 'moti';
import { BottomSheetModal, BottomSheetView } from '@gorhom/bottom-sheet';
import { YStack, XStack, Text, Separator, Label, Button, TextArea, ScrollView } from 'tamagui';
import { FontAwesome, MaterialIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import moment from 'moment';
import { LinearGradient } from 'expo-linear-gradient';
import * as Animatable from 'react-native-animatable';
import apis from '@api/api';
import EditEventModal, { EditEventModalRef } from './EditEventModal';
import { useNavigation } from '@react-navigation/native';

interface EventDetailsSliderProps {
  onClose?: () => void;
  onEventUpdated?: () => void;
  onEventDeleted?: () => void;
}

export interface EventDetailsSliderRef {
  showEvent: (event: any) => void;
  hideEvent: () => void;
}

const EventDetailsSlider = forwardRef<EventDetailsSliderRef, EventDetailsSliderProps>(
  ({ onClose, onEventUpdated, onEventDeleted }, ref) => {
    const { width, height } = useWindowDimensions();
    const theme = themeSelector();
    const navigation = useNavigation();
    const [selectedEvent, setSelectedEvent] = useState<any>(null);
    const [isVisible, setIsVisible] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const bottomSheetRef = useRef<BottomSheetModal>(null);
    const editModalRef = useRef<EditEventModalRef>(null);

    // Determine if we should use mobile layout (smaller screens)
    const isMobile = width < 768;
    const slideWidth = Math.min(450, width * 0.4);

    useImperativeHandle(ref, () => ({
      showEvent: (event: any) => {
        setSelectedEvent(event);
        setIsVisible(true);
        if (isMobile) {
          bottomSheetRef.current?.present();
        }
      },
      hideEvent: () => {
        setIsVisible(false);
        if (isMobile) {
          bottomSheetRef.current?.dismiss();
        }
        setTimeout(() => {
          setSelectedEvent(null);
        }, 300);
      }
    }), [isMobile]);

    const handleClose = () => {
      setIsVisible(false);
      if (isMobile) {
        bottomSheetRef.current?.dismiss();
      }
      onClose?.();
      setTimeout(() => {
        setSelectedEvent(null);
      }, 300);
    };

    const handleEdit = () => {
      if (selectedEvent) {
        editModalRef.current?.showModal(selectedEvent);
      }
    };

    const handleDelete = () => {
      if (!selectedEvent) return;

      Alert.alert(
        'Delete Event',
        `Are you sure you want to delete "${selectedEvent.title}"? This action cannot be undone.`,
        [
          {
            text: 'Cancel',
            style: 'cancel'
          },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              setIsDeleting(true);
              try {
                await apis.removeCalendarEntry(selectedEvent._id);
                handleClose();
                onEventDeleted?.();
              } catch (error) {
                console.error('Error deleting event:', error);
                Alert.alert('Error', 'Failed to delete event. Please try again.');
              } finally {
                setIsDeleting(false);
              }
            }
          }
        ]
      );
    };

    const handleEventUpdateComplete = () => {
      handleClose();
      onEventUpdated?.();
    };

    const getEventCategoryIcon = (event: any) => {
      if (event.isSystemEvent) {
        switch (event.category) {
          case 'project-deadline':
            return '📋';
          case 'flagged-task':
            return '🚩';
          case 'flagged-data':
            return '⭐';
          default:
            return '📅';
        }
      }
      return '📅';
    };

    const getEventPriorityColor = (event: any) => {
      if (event.isSystemEvent) {
        return event.color || '#007AFF';
      }
      return '#007AFF';
    };

    const EventContent = () => {
      if (!selectedEvent) return null;

      const eventIcon = getEventCategoryIcon(selectedEvent);
      const priorityColor = getEventPriorityColor(selectedEvent);

      return (
        <ScrollView
          style={{
            flex: 1,
            backgroundColor: theme ? '#fff' : '#1a1a1a',
          }}
          contentContainerStyle={{ padding: 20 }}
          showsVerticalScrollIndicator={false}
        >
          {/* Header */}
          <XStack alignItems="center" justifyContent="space-between" marginBottom="$4">
            <XStack alignItems="center" flex={1} gap="$3">
              <Text fontSize={24}>{eventIcon}</Text>
              <YStack flex={1}>
                <Text
                  fontSize={20}
                  fontWeight="600"
                  color={theme ? '#000' : '#fff'}
                  numberOfLines={2}
                >
                  {selectedEvent.title}
                </Text>
                {selectedEvent.isSystemEvent && (
                  <Text
                    fontSize={14}
                    color={priorityColor}
                    fontWeight="500"
                    textTransform="capitalize"
                  >
                    {selectedEvent.category?.replace('-', ' ')} • {selectedEvent.priority}
                  </Text>
                )}
              </YStack>
            </XStack>
            <Pressable
              onPress={handleClose}
              style={({ pressed }) => ({
                padding: 8,
                borderRadius: 20,
                backgroundColor: pressed
                  ? (theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)')
                  : 'transparent',
              })}
            >
              <FontAwesome 
                name="times" 
                size={20} 
                color={theme ? '#666' : '#999'} 
              />
            </Pressable>
          </XStack>

          <Separator backgroundColor={theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'} />

          {/* Event Details */}
          <YStack gap="$4" marginTop="$4">
            {/* Date and Time */}
            <YStack gap="$2">
              <Label color={theme ? '#666' : '#999'} fontSize={14} fontWeight="600">
                Date & Time
              </Label>
              <XStack gap="$4" flexWrap="wrap">
                <YStack gap="$1">
                  <Text fontSize={16} color={theme ? '#000' : '#fff'}>
                    📅 {moment(selectedEvent.startDate).format('MMMM Do, YYYY')}
                  </Text>
                  {!selectedEvent.allDay && (
                    <XStack gap="$4">
                      <Text fontSize={14} color={theme ? '#666' : '#999'}>
                        🕐 {moment(selectedEvent.startDate).format('h:mm A')}
                      </Text>
                      {selectedEvent.endDate && (
                        <Text fontSize={14} color={theme ? '#666' : '#999'}>
                          → {moment(selectedEvent.endDate).format('h:mm A')}
                        </Text>
                      )}
                    </XStack>
                  )}
                  {selectedEvent.allDay && (
                    <Text fontSize={14} color={theme ? '#666' : '#999'}>
                      All Day
                    </Text>
                  )}
                </YStack>
              </XStack>
            </YStack>

            {/* Location */}
            {selectedEvent.location && (
              <YStack gap="$2">
                <Label color={theme ? '#666' : '#999'} fontSize={14} fontWeight="600">
                  Location
                </Label>
                <Text fontSize={16} color={theme ? '#000' : '#fff'}>
                  📍 {selectedEvent.location}
                </Text>
              </YStack>
            )}

            {/* Description */}
            {selectedEvent.description && (
              <YStack gap="$2">
                <Label color={theme ? '#666' : '#999'} fontSize={14} fontWeight="600">
                  Description
                </Label>
                <Text fontSize={16} color={theme ? '#000' : '#fff'} lineHeight={22}>
                  {selectedEvent.description}
                </Text>
              </YStack>
            )}

            {/* Summary */}
            {selectedEvent.summary && (
              <YStack gap="$2">
                <Label color={theme ? '#666' : '#999'} fontSize={14} fontWeight="600">
                  Summary
                </Label>
                <Text fontSize={16} color={theme ? '#000' : '#fff'} lineHeight={22}>
                  {selectedEvent.summary}
                </Text>
              </YStack>
            )}

            {/* Data Item Link - Show if event has metadata with data item link */}
            {selectedEvent.metadata?.type === 'data_item_deadline' && selectedEvent.metadata?.dataItemId && (
              <YStack gap="$2">
                <Label color={theme ? '#666' : '#999'} fontSize={14} fontWeight="600">
                  Linked Data Item
                </Label>
                <Button
                  size="$4"
                  backgroundColor="#3b82f6"
                  color="white"
                  borderRadius={12}
                  onPress={() => {
                    handleClose();
                    // Navigate to the data item
                    (navigation as any).navigate('DataBrowser', {
                      requestId: selectedEvent.metadata.dataId,
                      title: 'Data Item',
                      type: undefined
                    });
                    // Then navigate to the specific item if possible
                    setTimeout(() => {
                      (navigation as any).navigate('GenericDataItemScreen', {
                        dataId: selectedEvent.metadata.dataId,
                        contentId: selectedEvent.metadata.dataItemId
                      });
                    }, 500);
                  }}
                  icon={<MaterialIcons name="open-in-new" size={18} color="white" />}
                >
                  Open Data Item
                </Button>
              </YStack>
            )}

            {/* System Event Details */}
            {selectedEvent.isSystemEvent && (
              <>
                <Separator backgroundColor={theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'} />
                
                {/* Project Info */}
                {selectedEvent.projectTitle && (
                  <YStack gap="$2">
                    <Label color={theme ? '#666' : '#999'} fontSize={14} fontWeight="600">
                      Project
                    </Label>
                    <Text fontSize={16} color={theme ? '#000' : '#fff'}>
                      {selectedEvent.projectTitle}
                    </Text>
                    {selectedEvent.workPackageTitle && (
                      <Text fontSize={14} color={theme ? '#666' : '#999'}>
                        Work Package: {selectedEvent.workPackageTitle}
                      </Text>
                    )}
                  </YStack>
                )}

                {/* Status */}
                {selectedEvent.status && (
                  <YStack gap="$2">
                    <Label color={theme ? '#666' : '#999'} fontSize={14} fontWeight="600">
                      Status
                    </Label>
                    <XStack alignItems="center" gap="$2">
                      <MaterialIcons
                        name={selectedEvent.status === 'active' ? 'play-circle-filled' : 'pause-circle-filled'}
                        size={16}
                        color={selectedEvent.status === 'active' ? '#4CAF50' : '#FF9800'}
                      />
                      <Text
                        fontSize={16}
                        color={theme ? '#000' : '#fff'}
                        textTransform="capitalize"
                      >
                        {selectedEvent.status}
                      </Text>
                    </XStack>
                  </YStack>
                )}

                {/* Days Until */}
                {selectedEvent.daysUntil !== undefined && (
                  <YStack gap="$2">
                    <Label color={theme ? '#666' : '#999'} fontSize={14} fontWeight="600">
                      Time Remaining
                    </Label>
                    <XStack alignItems="center" gap="$2">
                      <MaterialIcons
                        name="schedule"
                        size={16}
                        color={selectedEvent.daysUntil <= 3 ? '#F44336' : selectedEvent.daysUntil <= 7 ? '#FF9800' : '#4CAF50'}
                      />
                      <Text
                        fontSize={16}
                        color={selectedEvent.daysUntil <= 3 ? '#F44336' : selectedEvent.daysUntil <= 7 ? '#FF9800' : '#4CAF50'}
                        fontWeight="600"
                      >
                        {selectedEvent.daysUntil === 0 ? 'Today' : 
                         selectedEvent.daysUntil === 1 ? 'Tomorrow' :
                         `${selectedEvent.daysUntil} days`}
                      </Text>
                    </XStack>
                  </YStack>
                )}
              </>
            )}

            {/* Attendees */}
            {selectedEvent.attendees && selectedEvent.attendees.length > 0 && (
              <YStack gap="$2">
                <Label color={theme ? '#666' : '#999'} fontSize={14} fontWeight="600">
                  Attendees
                </Label>
                {selectedEvent.attendees.map((attendee: string, index: number) => (
                  <Text key={index} fontSize={16} color={theme ? '#000' : '#fff'}>
                    👤 {attendee}
                  </Text>
                ))}
              </YStack>
            )}

            {/* Notes */}
            {selectedEvent.notes && (
              <YStack gap="$2">
                <Label color={theme ? '#666' : '#999'} fontSize={14} fontWeight="600">
                  Notes
                </Label>
                <TextArea
                  value={selectedEvent.notes}
                  backgroundColor={theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)'}
                  borderColor={theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'}
                  borderRadius={8}
                  minHeight={80}
                  editable={false}
                />
              </YStack>
            )}

            {/* Actions */}
            <XStack gap="$3" marginTop="$4" flexWrap="wrap">
              {!selectedEvent.isSystemEvent && (
                <>
                  <Button
                    size="$4"
                    backgroundColor="#007AFF"
                    color="white"
                    borderRadius={12}
                    flexGrow={1}
                    onPress={handleEdit}
                    disabled={isDeleting}
                    icon={<MaterialIcons name="edit" size={18} color="white" />}
                  >
                    Edit Event
                  </Button>
                  <Button
                    size="$4"
                    backgroundColor="#FF3B30"
                    color="white"
                    borderRadius={12}
                    flexGrow={1}
                    onPress={handleDelete}
                    disabled={isDeleting}
                    opacity={isDeleting ? 0.5 : 1}
                    icon={<MaterialIcons name="delete" size={18} color="white" />}
                  >
                    {isDeleting ? 'Deleting...' : 'Delete'}
                  </Button>
                </>
              )}
              {selectedEvent.isSystemEvent && selectedEvent.projectId && (
                <Button
                  size="$4"
                  backgroundColor="#007AFF"
                  color="white"
                  borderRadius={12}
                  flexGrow={1}
                  icon={<MaterialIcons name="open-in-new" size={18} color="white" />}
                >
                  Open Project
                </Button>
              )}
              {selectedEvent.metadata?.type === 'data_item_deadline' && selectedEvent.metadata?.dataItemId && (
                <Button
                  size="$4"
                  backgroundColor="#3b82f6"
                  color="white"
                  borderRadius={12}
                  flexGrow={1}
                  onPress={() => {
                    handleClose();
                    // Navigate to the data item
                    (navigation as any).navigate('DataBrowser', {
                      requestId: selectedEvent.metadata.dataId,
                      title: 'Data Item',
                      type: undefined
                    });
                    // Then navigate to the specific item if possible
                    setTimeout(() => {
                      (navigation as any).navigate('GenericDataItemScreen', {
                        dataId: selectedEvent.metadata.dataId,
                        contentId: selectedEvent.metadata.dataItemId
                      });
                    }, 500);
                  }}
                  icon={<MaterialIcons name="open-in-new" size={18} color="white" />}
                >
                  Open Data Item
                </Button>
              )}
            </XStack>
          </YStack>

          {/* Bottom padding for mobile */}
          {isMobile && <YStack height={100} />}
        </ScrollView>
      );
    };

    // Render EditEventModal once, outside of conditional rendering
    return (
      <>
        {/* Desktop/Tablet slide-in view */}
        {!isMobile && (
          <>
            {/* Backdrop */}
            {isVisible && (
              <MotiView
                from={{ opacity: 0 }}
                animate={{ opacity: isVisible ? 0.5 : 0 }}
                exit={{ opacity: 0 }}
                transition={{ type: 'timing', duration: 200 }}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'black',
                  zIndex: 999,
                }}
                pointerEvents={isVisible ? 'auto' : 'none'}
              >
                <Pressable style={{ flex: 1 }} onPress={handleClose} />
              </MotiView>
            )}

            {/* Slide-in panel */}
            <MotiView
              from={{ translateX: slideWidth }}
              animate={{ translateX: isVisible ? 0 : slideWidth }}
              exit={{ translateX: slideWidth }}
              transition={{ type: 'timing', duration: 300 }}
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: slideWidth,
                height: '100%',
                backgroundColor: theme ? '#fff' : '#1a1a1a',
                shadowColor: '#000',
                shadowOffset: isVisible && { width: -4, height: 0 },
                shadowOpacity: !isVisible ? 0 : (theme ? 0.15 : 0.25),
                shadowRadius: 16,
                elevation: 20,
                borderLeftWidth: 1,
                borderLeftColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                zIndex: 1000,
              }}
              pointerEvents={isVisible ? 'auto' : 'none'}
            >
              <EventContent />
            </MotiView>
          </>
        )}

        {/* Mobile bottom sheet */}
        {isMobile && (
          <BottomSheetModal
            ref={bottomSheetRef}
            snapPoints={['75%', '90%']}
            enablePanDownToClose
            enableDismissOnClose
            backgroundStyle={{
              backgroundColor: theme ? '#fff' : '#1a1a1a',
            }}
            handleIndicatorStyle={{
              backgroundColor: theme ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.3)',
            }}
            style={{
              shadowColor: '#000',
              shadowOffset: { width: 0, height: -4 },
              shadowOpacity: theme ? 0.15 : 0.25,
              shadowRadius: 16,
              elevation: 20,
            }}
            onDismiss={() => {
              setIsVisible(false);
              setSelectedEvent(null);
              onClose?.();
            }}
          >
            <BottomSheetView style={{ flex: 1 }}>
              <EventContent />
            </BottomSheetView>
          </BottomSheetModal>
        )}

        {/* Edit Modal - Always rendered at high z-index */}
        <EditEventModal ref={editModalRef} onEventUpdated={handleEventUpdateComplete} />
      </>
    );
  }
);

EventDetailsSlider.displayName = 'EventDetailsSlider';

export default EventDetailsSlider;
